import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import _ from "lodash";

export interface ACLState {
  [key: string]: "MODIFY" | "VIEW";
}

const initialState: ACLState = {};

function mapResourcesToAccessLevels(array: any[]) {
  return array?.reduce((acc, item) => {
    const { resource, access } = item;

    // If the resource key does not exist in acc,
    // or if the access is "MODIFY", set it to "MODIFY"
    if (!acc[resource] || access === "MODIFY") {
      acc[resource] = access;
    }

    return acc;
  }, {});
}

interface AccessEntry {
  access: "MODIFY" | "VIEW";
  createdAt: string;
  createdBy: string;
  id: number;
  resource: string;
  updatedAt: string;
  updatedBy: string;
}

// Function to generate a permissions object with "MODIFY" access for each available resource (for master admin)
const getAllResourcePermissionsForMasterAdmin = (
  availableResources: { code: string }[],
) => {
  return Object.assign(
    {},
    // Ensure availableResources is an array before mapping
    ...(availableResources
      ? // Map over available resources and assign "MODIFY" access level to each resource code
        availableResources.map(({ code }) => ({ [code]: "MODIFY" }))
      : []),
  );
};

// Function to get the permissions map for a non-master admin using a helper function
const getPermissionsForNonMasterAdmin = (adminPermissions: AccessEntry[]) => {
  return _.cloneDeep(mapResourcesToAccessLevels(adminPermissions));
};

export const ACLSlice = createSlice({
  name: "ACL",
  initialState,
  reducers: {
    setACL: (state, action: PayloadAction<any>) => {
      const isMaster = action?.payload?.roles?.master;
      const availableResources = action?.payload?.resources;
      const adminPermissions = action?.payload?.roles?.permissions;

      // Function to create access permissions object based on user type (master admin or non-master admin)
      return (state = isMaster
        ? getAllResourcePermissionsForMasterAdmin(availableResources)
        : getPermissionsForNonMasterAdmin(adminPermissions));
    },
  },
});

export const { setACL } = ACLSlice.actions;

export const selectRoles = (state: RootState) => state.ACL;

export default ACLSlice.reducer;
